import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Check, X, HandHelping, Undo2, Users, Bell, ChevronDown, Clock, Maximize2, Building2, RefreshCw } from 'lucide-react';
import { getAvatarDataUri } from '../lib/avatarPresets';
import { CategoryIcon } from '../lib/categoryIcons';

// ─── Avatar ───────────────────────────────────────────────
const colorMap: Record<string, { bg: string; color: string }> = {
  green:  { bg: 'var(--green-light)',  color: 'var(--green-dark)' },
  blue:   { bg: 'var(--blue-light)',   color: 'var(--blue)' },
  purple: { bg: 'var(--purple-light)', color: 'var(--purple)' },
  orange: { bg: 'var(--orange-light)', color: 'var(--orange)' },
  teal:   { bg: 'var(--teal-light)',   color: 'var(--teal)' },
  rose:   { bg: 'var(--rose-light)',   color: 'var(--rose)' },
};

const initials = (name: string) =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

interface AvatarProps { name: string; color?: string; size?: 'sm' | 'md' | 'lg'; imageUrl?: string | null; }
const sizes = { sm: 22, md: 38, lg: 64 };
const fSizes = { sm: 10, md: 14, lg: 22 };

const emojiSizes = { sm: 13, md: 22, lg: 36 };

export const Avatar: React.FC<AvatarProps> = ({ name, color = 'green', size = 'md', imageUrl }) => {
  const { bg, color: c } = colorMap[color] || colorMap.green;
  const px = sizes[size]; const fs = fSizes[size];
  if (imageUrl?.startsWith('dicebear:')) {
    const presetId = imageUrl.slice(9);
    const src = getAvatarDataUri(presetId);
    return <img src={src} alt={name} style={{ width: px, height: px, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, display: 'inline-block' }} />;
  }
  if (imageUrl?.startsWith('preset:')) {
    const emoji = imageUrl.slice(7);
    return (
      <div style={{ width: px, height: px, borderRadius: '50%', background: bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: emojiSizes[size], flexShrink: 0 }}>
        {emoji}
      </div>
    );
  }
  if (imageUrl) {
    return (
      <img src={imageUrl} alt={name} style={{ width: px, height: px, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, display: 'inline-block' }} />
    );
  }
  return (
    <div style={{ width: px, height: px, borderRadius: '50%', background: bg, color: c, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: fs, fontWeight: 600, flexShrink: 0 }}>
      {initials(name)}
    </div>
  );
};

// ─── Badge ────────────────────────────────────────────────
type BadgeVariant = 'green' | 'amber' | 'red' | 'blue';
const badgeStyles: Record<BadgeVariant, { bg: string; color: string; border: string }> = {
  green: { bg: '#B8EDAF', color: '#0F3008', border: '#6BBD60' },
  amber: { bg: '#FFD07A', color: '#5C2800', border: '#E08A00' },
  red:   { bg: '#FFB3B3', color: '#6B0000', border: '#E03030' },
  blue:  { bg: '#B8EDAF', color: '#0F3008', border: '#6BBD60' },
};
export const Badge: React.FC<{ variant: BadgeVariant; children: React.ReactNode }> = ({ variant, children }) => {
  const { bg, color, border } = badgeStyles[variant];
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 12px', borderRadius: 30, fontSize: 13, fontWeight: 700, background: bg, color, border: `1.5px solid ${border}` }}>{children}</span>;
};

// ─── Buttons ──────────────────────────────────────────────
export const PrimaryBtn: React.FC<{ onClick?: () => void; children: React.ReactNode; disabled?: boolean; type?: 'button' | 'submit' }> = ({ onClick, children, disabled, type = 'button' }) => (
  <button type={type} onClick={onClick} disabled={disabled} style={{ width: '100%', padding: 13, background: disabled ? 'rgba(255,255,255,0.12)' : 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: disabled ? 'var(--text3)' : '#ffffff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', boxShadow: disabled ? 'none' : '0 4px 16px rgba(34,197,94,0.35)' }}>
    {children}
  </button>
);

export const SecondaryBtn: React.FC<{ onClick?: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button onClick={onClick} style={{ width: '100%', padding: 13, marginTop: 10, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 500 }}>
    {children}
  </button>
);

export const IconBtn: React.FC<{ onClick?: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button onClick={onClick} style={{ background: 'none', border: 'none', padding: 7, borderRadius: 'var(--radius-xs)', fontSize: 20, cursor: 'pointer', color: 'var(--text)' }}>
    {children}
  </button>
);

// ─── Field ────────────────────────────────────────────────
export const Field: React.FC<{ label: React.ReactNode; error?: string; children: React.ReactNode }> = ({ label, error, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text2)', marginBottom: 5 }}>{label}</label>
    {children}
    {error && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>{error}</div>}
  </div>
);

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 13px',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 15, background: 'var(--surface)',
  color: 'var(--text)', outline: 'none',
};

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input {...props} style={{ ...inputStyle, ...props.style }} />
);

export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (props) => (
  <select {...props} style={{ ...inputStyle, ...props.style }} />
);

// ─── UnitPicker ───────────────────────────────────────────
export const UnitPicker: React.FC<{
  value: string;
  options: string[];
  onChange: (val: string) => void;
  placeholder?: string;
}> = ({ value, options, onChange, placeholder = 'stk' }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [dropRect, setDropRect] = useState<DOMRect | null>(null);
  const isCustom = value !== '' && !options.includes(value);

  const handleOpen = () => {
    if (btnRef.current) setDropRect(btnRef.current.getBoundingClientRect());
    setOpen(true);
  };

  const displayLabel = value || placeholder;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        style={{ ...inputStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, cursor: 'pointer', padding: '11px 10px 11px 13px', width: '100%' }}
      >
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayLabel}</span>
        <ChevronDown size={16} color="var(--text3)" strokeWidth={2} style={{ flexShrink: 0, transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && dropRect && (() => {
        const spaceBelow = window.innerHeight - dropRect.bottom - 8;
        const spaceAbove = dropRect.top - 8;
        const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
        const maxH = Math.min(260, Math.max(120, openUp ? spaceAbove : spaceBelow));
        return ReactDOM.createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1998 }} />
          <div style={{
            position: 'fixed',
            ...(openUp ? { bottom: window.innerHeight - dropRect.top + 4 } : { top: dropRect.bottom + 4 }),
            right: window.innerWidth - dropRect.right,
            minWidth: Math.max(dropRect.width, 130),
            zIndex: 1999,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
            maxHeight: maxH,
            overflowY: 'auto',
          }}>
            {options.map((opt, i) => {
              const active = value === opt;
              return (
                <button key={opt} type="button"
                  onClick={() => { onChange(opt); setOpen(false); }}
                  style={{ width: '100%', padding: '11px 14px', border: 'none', borderTop: i > 0 ? '1px solid var(--border)' : 'none', background: 'none', color: active ? 'var(--green)' : 'var(--text)', fontSize: 14, fontWeight: active ? 600 : 400, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  {opt}
                  {active && <Check size={14} color="var(--green)" strokeWidth={2.5} />}
                </button>
              );
            })}
            <button type="button"
              onClick={() => { onChange('__custom__'); setOpen(false); }}
              style={{ width: '100%', padding: '11px 14px', border: 'none', borderTop: '1px solid var(--border)', background: 'none', color: isCustom ? 'var(--green)' : 'var(--text2)', fontSize: 14, fontWeight: isCustom ? 600 : 400, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              Annen...
              {isCustom && <Check size={14} color="var(--green)" strokeWidth={2.5} />}
            </button>
          </div>
        </>,
        document.body
        );
      })()}
    </>
  );
};

export const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => (
  <textarea {...props} style={{ ...inputStyle, resize: 'none', ...props.style }} />
);

// ─── Card ─────────────────────────────────────────────────
export const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties; onClick?: () => void }> = ({ children, style, onClick }) => (
  <div onClick={onClick} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 14, marginBottom: 10, cursor: onClick ? 'pointer' : 'default', backdropFilter: 'none', ...style }}>
    {children}
  </div>
);

// ─── ImageTile (kort-grid: bilde øverst, hvit kortkropp med navn under) ──
interface ImageTileProps {
  imageUrl?: string | null;
  emoji?: string | null;
  name: React.ReactNode;
  subtitle?: React.ReactNode;
  onClick: () => void;
  topLeft?: React.ReactNode;
  ribbon?: React.ReactNode;
  ribbonColor?: string;
  statusColor?: string;
  statusBg?: string;
  statusLabel?: string;
  StatusIcon?: React.ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  ActionIcon?: React.ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  onAction?: () => void;
  actionLabel?: string;
  onExpand?: () => void;
  orgBadge?: { name: string };
  groupBadge?: { names: string[] };
}
export const ImageTile: React.FC<ImageTileProps> = ({ imageUrl, emoji, name, subtitle, onClick, topLeft, ribbon, ribbonColor, statusColor, statusBg, statusLabel, StatusIcon, ActionIcon, onAction, actionLabel, onExpand, orgBadge, groupBadge }) => {
  const [showBadgePopup, setShowBadgePopup] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const badgeBtnRef = useRef<HTMLButtonElement>(null);
  const popupWidth = 220;

  const badgeText = orgBadge ? `Eies av ${orgBadge.name}` : (groupBadge && groupBadge.names.length > 0 ? `Delt i ${groupBadge.names.join(', ')}` : null);
  const BadgeIcon = orgBadge ? Building2 : Users;

  const openBadgePopup = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = badgeBtnRef.current?.getBoundingClientRect();
    if (rect) {
      setPopupPos({
        top: rect.top - 8,
        left: Math.min(Math.max(rect.left, 8), window.innerWidth - popupWidth - 8),
      });
    }
    setShowBadgePopup(s => !s);
  };

  return (
  <div
    onClick={onClick}
    style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
      overflow: 'hidden', cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column',
    }}
  >
    <div style={{
      position: 'relative', aspectRatio: '16 / 10',
      background: imageUrl ? `url(${imageUrl}) center / cover no-repeat` : 'var(--green-light)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {!imageUrl && <CategoryIcon emoji={emoji} size={34} color="var(--green)" />}

      {ribbon && (
        <div style={{
          position: 'absolute', top: 10, left: -30, width: 110,
          background: ribbonColor || 'var(--accent)', color: '#fff', fontSize: 9.5, fontWeight: 700,
          textAlign: 'center', padding: '2px 0', transform: 'rotate(-45deg)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        }}>
          {ribbon}
        </div>
      )}

      {imageUrl && onExpand && (
        <button
          onClick={e => { e.stopPropagation(); onExpand(); }}
          style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: 6, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-in' }}
        >
          <Maximize2 size={12} color="#fff" strokeWidth={2} />
        </button>
      )}

      {badgeText && (
        <button
          ref={badgeBtnRef}
          onClick={openBadgePopup}
          style={{ position: 'absolute', bottom: 6, left: 6, background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: 6, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <BadgeIcon size={12} color="#fff" strokeWidth={2} />
        </button>
      )}
    </div>

    {badgeText && showBadgePopup && ReactDOM.createPortal(
      <>
        <div onClick={e => { e.stopPropagation(); setShowBadgePopup(false); }} style={{ position: 'fixed', inset: 0, zIndex: 299 }} />
        {/* Portal-rendret (ikke inni kortet) siden kortet selv har overflow:hidden —
            en popup nøstet inni ville blitt kappet av kortkanten på lange navn/lister. */}
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed', top: popupPos.top, left: popupPos.left, transform: 'translateY(-100%)',
            maxWidth: popupWidth, width: 'max-content', background: 'rgba(0,0,0,0.9)', color: '#fff', fontSize: 12, fontWeight: 600,
            padding: '8px 11px', borderRadius: 8, zIndex: 300, lineHeight: 1.4, boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          }}
        >
          {badgeText}
        </div>
      </>,
      document.body
    )}

    <div style={{ padding: '8px 10px 10px', flex: 1, display: 'flex', flexDirection: 'column' }}>
      {topLeft && <div style={{ marginBottom: 4 }}>{topLeft}</div>}

      <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text)', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }}>
        {name}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>
      )}

      {(statusLabel || actionLabel) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 'auto', paddingTop: 7 }}>
          {StatusIcon && statusColor && statusLabel ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 20, background: statusBg || 'var(--bg)', color: statusColor, fontSize: 10.5, fontWeight: 700, flex: '0 1 auto', minWidth: 0 }}>
              <StatusIcon size={11} color={statusColor} strokeWidth={2.5} style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{statusLabel}</span>
            </span>
          ) : <span />}

          {ActionIcon && onAction && actionLabel && (
            <button
              onClick={e => { e.stopPropagation(); onAction(); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 9px', borderRadius: 20, background: 'var(--green)', color: '#fff', border: 'none', fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0 }}
            >
              <ActionIcon size={11} color="#fff" strokeWidth={2.5} /> {actionLabel}
            </button>
          )}
        </div>
      )}
    </div>
  </div>
  );
};

// ─── ImageTileBadge (liten pill til bruk i topLeft-slot: eier/gruppe/antall) ──
export const ImageTileBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, background: 'var(--bg)', color: 'var(--text2)', fontSize: 11, fontWeight: 600 }}>
    {children}
  </span>
);

// ─── DueSoonBanner (varsler om lån som forfaller i morgen, lukkbar) ──
export const DueSoonBanner: React.FC<{ items: { id: string; navn: string; due_date: string | null; status: string }[] }> = ({ items }) => {
  const [dismissed, setDismissed] = useState<string | null>(null);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const dueTomorrow = items
    .filter(i => i.status === 'loaned' && i.due_date && i.due_date.startsWith(tomorrowStr))
    .sort((a, b) => a.navn.localeCompare(b.navn))[0];

  if (!dueTomorrow) return null;

  const key = `due_banner_dismissed_${dueTomorrow.id}_${dueTomorrow.due_date}`;
  if (dismissed === key || localStorage.getItem(key)) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 12 }}>
      <Clock size={16} color="var(--amber)" strokeWidth={2} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: 13, color: 'var(--amber)', fontWeight: 500, lineHeight: 1.4 }}>
        Husk å levere tilbake <strong>{dueTomorrow.navn}</strong> i morgen
      </span>
      <button
        onClick={() => { localStorage.setItem(key, '1'); setDismissed(key); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', color: 'var(--amber)', flexShrink: 0 }}
      >
        <X size={14} strokeWidth={2.2} />
      </button>
    </div>
  );
};

// ─── BottomSheet ──────────────────────────────────────────
export const BottomSheet: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => {
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startYRef = useRef(0);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const onTouchStart = (e: React.TouchEvent) => {
    startYRef.current = e.touches[0].clientY;
    setDragging(true);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const delta = e.touches[0].clientY - startYRef.current;
    if (delta > 0) setDragY(delta);
  };
  const onTouchEnd = () => {
    setDragging(false);
    if (dragY > 120) { onClose(); } else { setDragY(0); }
  };

  return ReactDOM.createPortal(
    <div onClick={onClose} className="bottomsheet-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div
        onClick={e => e.stopPropagation()}
        className="bottomsheet-panel"
        style={{
          background: 'var(--surface-solid)', borderRadius: '20px 20px 0 0',
          padding: '0 16px calc(env(safe-area-inset-bottom, 0px) + 32px)',
          width: '100%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', overscrollBehavior: 'contain',
          position: 'relative',
          transform: `translateY(${dragY}px)`,
          transition: dragging ? 'none' : 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        <div
          className="bottomsheet-drag-handle"
          onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
          style={{ padding: '18px 0 14px', cursor: 'grab', touchAction: 'none', textAlign: 'center' }}
        >
          <div style={{ width: 36, height: 4, background: 'var(--border-strong)', borderRadius: 2, display: 'inline-block' }} />
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 18, marginTop: 6 }}>{title}</h2>
        {children}
      </div>
    </div>,
    document.body
  );
};

// ─── Spinner ──────────────────────────────────────────────
export const Spinner: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
    <div style={{ width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: 'var(--green)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ─── PullToRefresh ────────────────────────────────────────
// Egen implementasjon siden nettleserens innebygde pull-to-refresh
// forsvinner helt når appen er lagt til på hjemskjermen (PWA/standalone-modus
// har ingen nettleser-chrome å vise indikatoren i). Full window.location.reload()
// brukes bevisst (ikke bare re-fetch av data) — gir samtidig en lett tilgjengelig
// manuell "nødbrems" mot fastlåst JS-tilstand, se stuck-loading-fiksen i App.tsx.
export const PullToRefresh: React.FC<{ id?: string; style?: React.CSSProperties; children: React.ReactNode }> = ({ id, style, children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const isPulling = useRef(false);
  const currentPull = useRef(0);

  const THRESHOLD = 70;
  const MAX_PULL = 100;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if (refreshing) return;
      if (el.scrollTop <= 0) {
        startY.current = e.touches[0].clientY;
        isPulling.current = true;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isPulling.current || startY.current === null || refreshing) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta > 0 && el.scrollTop <= 0) {
        e.preventDefault();
        const damped = Math.min(MAX_PULL, delta * 0.5);
        currentPull.current = damped;
        setPullDistance(damped);
      } else {
        isPulling.current = false;
        startY.current = null;
        currentPull.current = 0;
        setPullDistance(0);
      }
    };

    const onTouchEnd = () => {
      if (!isPulling.current) return;
      isPulling.current = false;
      startY.current = null;
      if (currentPull.current >= THRESHOLD) {
        setRefreshing(true);
        setPullDistance(THRESHOLD);
        setTimeout(() => window.location.reload(), 350);
      } else {
        currentPull.current = 0;
        setPullDistance(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [refreshing]);

  const progress = Math.min(1, pullDistance / THRESHOLD);

  return (
    <div id={id} ref={containerRef} style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative', ...style }}>
      <div style={{
        height: pullDistance, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        overflow: 'hidden', transition: isPulling.current ? 'none' : 'height 0.2s ease', flexShrink: 0,
      }}>
        <div style={{ paddingBottom: 8, display: 'flex', alignItems: 'center', gap: 6, opacity: progress }}>
          {refreshing ? (
            <div style={{ width: 16, height: 16, border: '2px solid var(--border)', borderTopColor: 'var(--green)', borderRadius: '50%', animation: 'ptrSpin 0.7s linear infinite' }} />
          ) : (
            <RefreshCw size={15} color="var(--text3)" strokeWidth={2} style={{ transform: `rotate(${progress * 180}deg)`, transition: 'transform 0.1s linear' }} />
          )}
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)' }}>
            {refreshing ? 'Oppdaterer...' : progress >= 1 ? 'Slipp for å oppdatere' : 'Dra ned for å oppdatere'}
          </span>
        </div>
      </div>
      {children}
      <style>{`@keyframes ptrSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─── SectionLabel ─────────────────────────────────────────
export const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>{children}</p>
);

// ─── Divider ──────────────────────────────────────────────
export const Divider: React.FC = () => (
  <div style={{ height: 1, background: 'var(--border)', margin: '16px 0' }} />
);

// ─── QuantityStepper ──────────────────────────────────────
export const QuantityStepper: React.FC<{ value: string; onChange: (val: string) => void }> = ({ value, onChange }) => {
  const num = parseInt(value) || 0;
  const btnStyle: React.CSSProperties = {
    width: 44, flexShrink: 0, border: 'none', background: 'transparent',
    color: 'var(--text2)', fontSize: 22, fontWeight: 300, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'color 0.15s, background 0.15s', userSelect: 'none',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)', overflow: 'hidden', height: 44 }}>
      <button type="button" onClick={() => onChange(String(Math.max(0, num - 1)))} style={btnStyle}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text2)'; }}
      >−</button>
      <input
        type="number" min="0" step="1" value={value}
        onChange={e => onChange(e.target.value.replace(/[^0-9]/g, ''))}
        style={{ flex: 1, border: 'none', borderLeft: '1px solid var(--border-strong)', borderRight: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text)', fontSize: 17, fontWeight: 600, textAlign: 'center', outline: 'none', height: '100%', padding: 0, minWidth: 0 }}
      />
      <button type="button" onClick={() => onChange(String(num + 1))} style={btnStyle}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text2)'; }}
      >+</button>
    </div>
  );
};

// ─── ErrorMsg ─────────────────────────────────────────────
export const ErrorMsg: React.FC<{ msg: string }> = ({ msg }) => (
  <div style={{ background: 'var(--red-light)', color: 'var(--red)', padding: '10px 13px', borderRadius: 'var(--radius-sm)', fontSize: 14, marginBottom: 14 }}>{msg}</div>
);

// ─── ConfirmDialog + useConfirm ──────────────────────────
const ConfirmDialogUI: React.FC<{
  message: string;
  confirmLabel: string;
  danger: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ message, confirmLabel, danger, onConfirm, onCancel }) => ReactDOM.createPortal(
  <div
    onClick={onCancel}
    style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '0 24px',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: 'var(--surface-solid)',
        borderRadius: 'var(--radius)',
        padding: '28px 20px 20px',
        width: '100%', maxWidth: 320,
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        animation: 'dialogIn 0.15s ease',
      }}
    >
      <p style={{ fontSize: 15, fontWeight: 500, textAlign: 'center', marginBottom: 22, color: 'var(--text)', lineHeight: 1.5 }}>
        {message}
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1, padding: '12px 10px', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border-strong)', background: 'var(--bg)',
            fontSize: 15, fontWeight: 500, cursor: 'pointer',
          }}
        >
          Avbryt
        </button>
        <button
          onClick={onConfirm}
          style={{
            flex: 1, padding: '12px 10px', borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: danger ? 'var(--red)' : 'var(--green)',
            color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
    <style>{`@keyframes dialogIn { from { opacity:0; transform:scale(0.95); } to { opacity:1; transform:scale(1); } }`}</style>
  </div>,
  document.body
);

// ─── ImageLightbox ────────────────────────────────────────
export const ImageLightbox: React.FC<{ url: string; onClose: () => void }> = ({ url, onClose }) => ReactDOM.createPortal(
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, zIndex: 400,
      background: 'rgba(0,0,0,0.88)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}
  >
    <button
      onClick={onClose}
      style={{
        position: 'absolute', top: 16, right: 16,
        background: 'rgba(255,255,255,0.15)', border: 'none',
        borderRadius: '50%', width: 40, height: 40,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', color: '#fff', fontSize: 22, lineHeight: 1,
      }}
    >
      ×
    </button>
    <img
      src={url}
      alt=""
      onClick={e => e.stopPropagation()}
      style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 12, objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
    />
  </div>,
  document.body
);

export function useConfirm() {
  const [state, setState] = useState<{
    message: string;
    confirmLabel: string;
    danger: boolean;
    resolve: (v: boolean) => void;
  } | null>(null);

  const confirm = useCallback((
    message: string,
    opts?: { confirmLabel?: string; danger?: boolean }
  ): Promise<boolean> => {
    return new Promise(resolve => {
      setState({ message, confirmLabel: opts?.confirmLabel ?? 'Bekreft', danger: opts?.danger ?? true, resolve });
    });
  }, []);

  const handleConfirm = () => { state?.resolve(true); setState(null); };
  const handleCancel = () => { state?.resolve(false); setState(null); };

  const dialog = state ? (
    <ConfirmDialogUI
      message={state.message}
      confirmLabel={state.confirmLabel}
      danger={state.danger}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  ) : null;

  return { confirm, dialog };
}

// ─── ExpandSection ────────────────────────────────────────
export const ExpandSection: React.FC<{
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ label, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 14, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 14px', background: 'var(--surface)', border: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 600, color: 'var(--text2)',
        }}
      >
        {label}
        <span style={{ fontSize: 10, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
      </button>
      <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows 0.25s ease' }}>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px 4px' }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── TagInput ─────────────────────────────────────────────
export const TagInput: React.FC<{
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
}> = ({ value, onChange, suggestions = [], placeholder = 'Skriv en tagg og trykk Enter eller +' }) => {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addTag = () => {
    const t = input.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setInput('');
  };

  const removeTag = (t: string) => onChange(value.filter(x => x !== t));

  const filteredSuggestions = suggestions.filter(
    s => !value.includes(s) && (input === '' || s.toLowerCase().includes(input.toLowerCase()))
  );

  return (
    <div>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {value.map(t => (
            <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px 4px 12px', borderRadius: 30, fontSize: 13, background: 'var(--green)', border: '1px solid var(--green)', color: '#fff' }}>
              {t}
              <button type="button" onClick={() => removeTag(t)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center', color: '#fff', opacity: 0.8, lineHeight: 1 }}>
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
          style={{ width: '100%', boxSizing: 'border-box', padding: '9px 44px 9px 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'var(--surface)', color: 'var(--text)', outline: 'none' }}
        />
        <button type="button" onClick={addTag} disabled={!input.trim()}
          style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 30, height: 30, borderRadius: '50%', border: 'none', cursor: input.trim() ? 'pointer' : 'default', fontSize: 20, fontWeight: 700, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s, color 0.15s', background: input.trim() ? 'var(--green)' : 'var(--border)', color: input.trim() ? '#fff' : 'var(--text3)' }}>
          +
        </button>
      </div>
      {filteredSuggestions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {filteredSuggestions.map(s => (
            <button key={s} type="button"
              onClick={() => { onChange([...value, s]); setInput(''); inputRef.current?.focus(); }}
              style={{ padding: '4px 12px', borderRadius: 30, fontSize: 13, cursor: 'pointer', border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text2)' }}>
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── NotificationToast ────────────────────────────────────
export type ToastNotification = {
  id: string;
  type: string;
  message: string;
  inventory_id: string | null;
  tool_id: string | null;
};

export const NotificationToast: React.FC<{
  notification: ToastNotification;
  onDismiss: () => void;
  onApprove?: () => Promise<void>;
  onReject?: () => Promise<void>;
}> = ({ notification, onDismiss, onApprove, onReject }) => {
  const isRequest = notification.type === 'loan_request' && (onApprove || onReject);
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);

  useEffect(() => {
    if (isRequest) return;
    const t = setTimeout(onDismiss, 4500);
    return () => clearTimeout(t);
  }, [isRequest, onDismiss]);

  const typeIcon = () => {
    switch (notification.type) {
      case 'loan_request':  return <HandHelping size={18} color="var(--blue)" strokeWidth={2} />;
      case 'loan_approved': return <Check size={18} color="var(--green)" strokeWidth={2.5} />;
      case 'loan_rejected': return <X size={18} color="var(--red)" strokeWidth={2.5} />;
      case 'returned':      return <Undo2 size={18} color="var(--green)" strokeWidth={2} />;
      case 'joined':        return <Users size={18} color="var(--purple)" strokeWidth={2} />;
      default:              return <Bell size={18} color="var(--text3)" strokeWidth={2} />;
    }
  };

  const handleApprove = async () => {
    setBusy('approve');
    await onApprove?.();
    onDismiss();
  };
  const handleReject = async () => {
    setBusy('reject');
    await onReject?.();
    onDismiss();
  };

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', top: 'calc(var(--safe-top) + 68px)', left: '50%',
      transform: 'translateX(-50%)',
      width: 'calc(100% - 32px)', maxWidth: 448,
      background: 'var(--surface)', borderRadius: 'var(--radius)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12)',
      border: '1px solid var(--border)',
      zIndex: 300,
      animation: 'toastSlideIn 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-16px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 14px 12px' }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
          {typeIcon()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, marginBottom: 2 }}>{notification.message}</div>
          {!isRequest && <div style={{ fontSize: 12, color: 'var(--text3)' }}>Nå nettopp</div>}
        </div>
        <button onClick={onDismiss} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: 'var(--text3)', display: 'flex', flexShrink: 0 }}>
          <X size={16} strokeWidth={2} />
        </button>
      </div>
      {isRequest && (
        <div style={{ display: 'flex', gap: 8, padding: '0 14px 14px' }}>
          <button onClick={handleApprove} disabled={busy !== null}
            style={{ flex: 1, padding: '10px 0', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--green)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: busy === 'reject' ? 0.5 : 1 }}>
            {busy === 'approve' ? '...' : <><Check size={14} strokeWidth={2.5} /> Godkjenn</>}
          </button>
          <button onClick={handleReject} disabled={busy !== null}
            style={{ flex: 1, padding: '10px 0', borderRadius: 'var(--radius-sm)', border: '1px solid var(--red)', background: 'none', color: 'var(--red)', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: busy === 'approve' ? 0.5 : 1 }}>
            {busy === 'reject' ? '...' : <><X size={14} strokeWidth={2.5} /> Avvis</>}
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};

// ─── CameraCapture ───────────────────────────────────────────
export const CameraCapture: React.FC<{ onCapture: (file: File) => void; onClose: () => void }> = ({ onCapture, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [camError, setCamError] = useState('');

  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then(stream => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => { if (!cancelled) setReady(true); };
        }
      })
      .catch(err => {
        if (cancelled) return;
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setCamError('Nettleseren har ikke tilgang til kameraet.\n\nGå til telefonens Innstillinger → Personvern → Kamera, og slå på tilgang for nettleseren din (Edge, Chrome, osv.).');
        } else if (err.name === 'NotFoundError') {
          setCamError('Ingen kamera funnet på enheten.');
        } else {
          setCamError('Kunne ikke åpne kameraet. Bruk «Velg bilde» i stedet.');
        }
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      if (!blob) return;
      streamRef.current?.getTracks().forEach(t => t.stop());
      onCapture(new File([blob], 'kamerabilde.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
      {camError ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center', gap: 16 }}>
          <div style={{ fontSize: 48 }}>📷</div>
          <div style={{ color: '#fff', fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{camError}</div>
          <button onClick={onClose} style={{ marginTop: 8, padding: '12px 28px', borderRadius: 10, border: 'none', background: '#fff', color: '#000', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Lukk</button>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ flex: 1, objectFit: 'cover', width: '100%', display: 'block' }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <div style={{ padding: '24px 40px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#000' }}>
            <button onClick={onClose} style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.4)', color: '#fff', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={22} strokeWidth={2} />
            </button>
            <button
              onClick={handleCapture}
              disabled={!ready}
              style={{ width: 72, height: 72, borderRadius: '50%', background: ready ? '#fff' : 'rgba(255,255,255,0.3)', border: '4px solid rgba(255,255,255,0.6)', cursor: ready ? 'pointer' : 'default', transition: 'background 0.2s' }}
              aria-label="Ta bilde"
            />
            <div style={{ width: 52 }} />
          </div>
        </>
      )}
    </div>,
    document.body
  );
};
